import { IStoreState } from '../types';
import { createSelector } from 'reselect';
import { find, matches, chain } from 'lodash';
import { getIsNewDesignEnabled } from './user';

export const getTemplatesList = (state: IStoreState) => state.templates.items;

/** The new design shows a template's `newDisplayName` where it has one. */
export const getTemplateTitle = (
  template: { displayName: string; newDisplayName?: string },
  isNewDesign: boolean
): string => (isNewDesign && template.newDisplayName) || template.displayName;

/**
 * Template descriptions are written with the old title ("The Rose Template…"), so the new
 * design swaps in its own title wherever the old one appears.
 */
export const getTemplateDescription = (
  description: string,
  template: { displayName?: string; newDisplayName?: string },
  isNewDesign: boolean
): string => {
  if (!isNewDesign || !template.displayName || !template.newDisplayName) {
    return description;
  }

  return description.split(template.displayName).join(template.newDisplayName);
};

export const getAreTemplatesFetched = (state: IStoreState) => state.templates.isFetched;
export const getDevelopedTemplates = createSelector([getTemplatesList], (templates) =>
  chain(templates).filter({ inDevelopment: false }).orderBy(['order'], ['asc']).value()
);

// Old-design templates the redesign folded into one (Valencia). The backend hides them from
// new-design users, so a collection still on one of them is marked as its replacement.
const REPLACED_TEMPLATES: Record<string, string> = {
  SpringVibesYellow: 'SpringVibes',
  SpringVibesRose: 'SpringVibes',
  SpringVibesBlue: 'SpringVibes'
};

export const getSelectedTemplate = createSelector(
  getDevelopedTemplates,
  (templates) =>
    (name: string = '', type: string = '') =>
      find(templates, matches({ name, type })) ||
      (REPLACED_TEMPLATES[name] &&
        find(templates, matches({ name: REPLACED_TEMPLATES[name], type }))) ||
      {}
);

/** The same mapping for a stored template `_id` (`ScrAppBook2_SpringVibesRose`), as presets keep. */
export const getReplacementTemplateId = (id: string = ''): string => {
  const separator = id.indexOf('_');
  const replacement = REPLACED_TEMPLATES[id.slice(separator + 1)];

  return separator > 0 && replacement ? `${id.slice(0, separator)}_${replacement}` : id;
};

export const getPaidTemplatesOptions = createSelector(
  [getTemplatesList, getIsNewDesignEnabled],
  (templates, isNewDesign) => (currencySymbol: string) =>
    chain(templates)
      .filter({ isPurchable: true })
      .map((template) => ({
        text: `${getTemplateTitle(template, isNewDesign)} - ${template.price}${currencySymbol}`,
        value: template._id
      }))
      .value()
);

/** Template prices come with Stripe's product name; the new design shows the template's own title. */
export const getTemplateTitleById = createSelector(
  [getTemplatesList, getIsNewDesignEnabled],
  (templates, isNewDesign) => (templateId: string, fallback: string) => {
    const template = find(templates, { _id: templateId });

    return template ? getTemplateTitle(template, isNewDesign) : fallback;
  }
);

export const getTemplatesFonts = (state: IStoreState) => state.templates.fonts;
